/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Labels for a calling number (an outbound caller ID or a tenant's number).
 *
 * Twilio often sets a number's friendly name to the number itself, so
 * "name · number" printed the same digits twice ("61495030099 · +61495030099").
 * The name is shown only when it adds something.
 */

const digits = (value) => String(value || "").replace(/\D/g, "");

/** The friendly name, or null when it is just the number written another way. */
export function distinctFriendlyName(number) {
  const name = String(number?.friendly_name || "").trim();
  if (!name) return null;
  const nameDigits = digits(name);
  const e164Digits = digits(number?.e164 || number?.phone_number);
  // A name with letters in it ("Sydney office") always counts.
  if (/[a-z]/i.test(name) || !nameDigits || !e164Digits) return name;
  const national = nameDigits.replace(/^0+/, "");
  const sameNumber =
    nameDigits === e164Digits || (national.length >= 6 && e164Digits.endsWith(national));
  return sameNumber ? null : name;
}

/** "Name · +61…" when the name adds something, otherwise just "+61…". */
export function callerIdLabel(number) {
  const e164 = number?.e164 || number?.phone_number || "";
  const name = distinctFriendlyName(number);
  return name ? `${name} · ${e164}` : e164;
}
